/**
 * Campaign templates (§8, §20.4) — presets that assemble a campaign DAG so a
 * proven workflow is one click for every workspace, not hand-wired each time.
 *
 * The first template is the one the platform's automation moat rests on:
 * "Review, then publish" — a reviewer sees the exact post at an approval gate,
 * and approving it releases the publish node, so approval IS the share. It was
 * hand-built during a live rollout; here it becomes a reusable product feature.
 *
 * A template only produces the DAG a normal campaign-create accepts — there is no
 * privileged path — so anything a template does, the visual builder could too.
 */

export interface CampaignTemplateInput {
  /** The asset the campaign reviews and publishes. */
  readonly assetId: string;
  /** The destination (channel) the approved asset publishes to. */
  readonly destinationId: string;
}

export interface CampaignTemplate {
  readonly id: string;
  readonly name: string;
  /** One line describing what running it does. */
  readonly description: string;
  /** Assemble the campaign body from the operator's choices. */
  build(input: CampaignTemplateInput, name: string): { name: string; dag: { nodes: unknown[] } };
}

/**
 * review (approve gate) → share (publish). The share depends on the review, so
 * the run pauses at the gate and the approval is what fires the publish.
 */
export const REVIEW_PUBLISH_TEMPLATE: CampaignTemplate = {
  id: 'review-publish',
  name: 'Review, then publish',
  description:
    'A reviewer sees the exact post and approves it — approval publishes it in one click.',
  build: ({ assetId, destinationId }, name) => ({
    name,
    dag: {
      nodes: [
        { nodeId: 'review', action: 'approve', inputs: {}, dependsOn: [] },
        {
          nodeId: 'share',
          action: 'publish',
          inputs: { assetId, destinationId },
          dependsOn: ['review'],
        },
      ],
    },
  }),
};

/** Every template offered in the "New from template" flow. */
export const CAMPAIGN_TEMPLATES: readonly CampaignTemplate[] = [REVIEW_PUBLISH_TEMPLATE];
